import React from 'react'
import _ from 'lodash/fp'
import { connect } from 'react-redux'
import { OAUTH_PATH, RETURN_PATH } from '../../config'

import * as connectedUnionsReducer from '../../reducers/connectedUnions'

import colors from '@fe/ui/colors'

import { Snackbar, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@material-ui/core'

import { builder, BuilderComponent } from '@builder.io/react'

builder.init('32eea804311a4d17817f2e534ded7af8')

const snackbarStyle = (severity) => ({
  backgroundColor: severity === 'error' ? colors.lightRed : severity === 'success' ? colors.lightGreen : colors.secondaryDarkGrey,
  color: colors.white,
  padding: '6px 16px',
  borderRadius: 4,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center'
})

const OrganisationsEdit = ({ classes, orgs, profile, ...props }) => {
  const searchParams = new URLSearchParams(_.get('location.search', props))
  const presetOrg = searchParams.get('org')
  const [activeOrg, setActiveOrg] = React.useState()
  const [snackbarOpen, setSnackbarOpen] = React.useState()

  React.useEffect(() => {
    props.getOrganizationsRequest()
  }, [])

  React.useEffect(() => {
    if (!_.isEmpty(props.toast)) {
      setSnackbarOpen(true)
    }
  }, [props.toast])

  React.useEffect(() => {
    if (presetOrg && !_.isEmpty(orgs)) {
      const match = _.find({ id: presetOrg }, orgs)
      if (!_.isEmpty(match)) {
        setActiveOrg(match)
      }
    }
  }, [orgs, presetOrg])

  const isMember = React.useCallback((org) => {
    const orgId = _.get('id', org)
    return _.flow(
      _.get('connections'),
      _.includes(orgId)
    )(profile)
  }, [profile])
  const isMemberOfActive = React.useMemo(() => isMember(activeOrg), [activeOrg, isMember])

  const handleCloseSnackbar = React.useCallback(() => {
    setSnackbarOpen(false)
    props.resetToast()
  }, [props.resetToast])

  const updateSearchParams = React.useCallback((searchParams) => {
    props.history.replace({
      path: props.location.path,
      search: searchParams
    })
  }, [props.history, props.location])

  const handleOrgClick = React.useCallback((org) => () => {
    const member = isMember(org)
    updateSearchParams(`?org=${_.get('id', org)}&connected=${member}`)
    setActiveOrg(org)
  }, [orgs, setActiveOrg, props.history, isMember])

  const handleClose = React.useCallback(() => {
    updateSearchParams()
    setActiveOrg(undefined)
  }, [setActiveOrg, props.history])

  const handleDisconnect = React.useCallback(() => {
    props.disconnectRequest(_.get('id', activeOrg))
    updateSearchParams()
    setActiveOrg(undefined)
  }, [activeOrg, props.disconnectRequest, props.history])

  const handleSync = React.useCallback(() => {
    props.syncUserInfo(_.get('id', activeOrg))
    updateSearchParams()
    setActiveOrg(undefined)
  }, [activeOrg, props.syncUserInfo, props.history])

  // TODO handle non-oauth orgs when they arrive
  const oauthUrl = React.useMemo(() => {
    return `${OAUTH_PATH(_.get('id', activeOrg))}/start?userId=${_.get('id', profile)}&platform=glfr&returnTo=${RETURN_PATH}/u/${_.get('handle', profile)}/profile`
  }, [activeOrg, profile])

  if (_.isEmpty(orgs)) {
    return false
  }

  return (
    <>
      <Snackbar
        onClose={handleCloseSnackbar}
        ContentProps={{
          style: snackbarStyle(_.get('severity', props.toast))
        }}
        action={(
          <Button
            style={{ fontWeight: '600' }}
            onClick={handleCloseSnackbar}
          >
            OK
          </Button>
        )}
        message={_.get('text', props.toast)}
        open={snackbarOpen}
      />

      <Dialog onClose={handleClose} open={!_.isEmpty(activeOrg)}>
        <DialogTitle>
          {`Connect with ${_.get('name', activeOrg)}`}
        </DialogTitle>
        <DialogContent>
          <BuilderComponent
            model='my-glfr-dynamic-content'
          />
        </DialogContent>
        <DialogActions>
          {!isMemberOfActive && (<Button href={oauthUrl}>Connect</Button>)}
          {isMemberOfActive && (<Button onClick={handleDisconnect}>Disconnect</Button>)}
          {isMemberOfActive && (<Button onClick={handleSync}>Synchronise</Button>)}
        </DialogActions>
      </Dialog>

      <div className={classes.fieldContainer} style={{ marginBottom: 24 }}>
        <label style={{ color: 'rgba(0,0,0,0.54)', fontWeight: '400', alignSelf: 'flex-start', paddingTop: 16 }}>
          ORGANISATIONS
        </label>
        {_.map((org) => {
          const m = isMember(org)
          return (
            <div
              key={_.get('id', org)}
              style={{ width: '100%', display: 'flex', justifyContent: 'space-between', marginTop: 8 }}
            >
              <Typography>
                {_.get('name', org)}
              </Typography>
              <Button
                disabled={props.syncing}
                style={{ color: props.syncing ? colors.secondaryLightGrey : colors.lightGreen, fontWeight: '600' }}
                onClick={handleOrgClick(org)}
              >
                {m ? 'Manage connection' : 'Connect'}
              </Button>

            </div>
          )
        }, orgs)}
      </div>
    </>
  )
}

export default _.compose(
  connect(
    (s) => ({
      orgs: connectedUnionsReducer.selectors.organizations(s),
      toast: connectedUnionsReducer.selectors.toast(s),
      syncing: connectedUnionsReducer.selectors.syncing(s)
    }),
    (d) => ({
      getOrganizationsRequest: () => d(connectedUnionsReducer.actions.getOrganizationsRequest()),
      disconnectRequest: (orgId) => d(connectedUnionsReducer.actions.disconnectRequest({ union: orgId })),
      syncUserInfo: (orgId) => d(connectedUnionsReducer.actions.syncUserInfoRequest({ union: orgId })),
      resetToast: () => d(connectedUnionsReducer.actions.setToast({}))
    })
  )
)(OrganisationsEdit)
